import apiClient from './apiClient';

export interface User {
  _id: string;
  name: string;
  email: string;
  phone: string;
  username: string;
  profileImage?: string;
  bio?: string;
  website?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  isPrivate: boolean;
  isVerified: boolean;
  isAdmin: boolean;
  postsCount: number;
  followersCount: number;
  followingCount: number;
  petTypePreferences: {
    dairyPets: boolean;
    companionPets: boolean;
  };
  preferredPetCategories: string[];
  isOnline?: boolean;
  lastSeen?: string;
  createdOn?: string;
}

export interface LoginRequest {
  name: string;
  email: string;
  phone: string;
  rememberMe?: boolean;
}

export interface AuthResponse {
  token: string;
  refreshToken: string;
  user: User;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error?: string;
}

class AuthService {
  private tokenKey = 'accessToken';
  private refreshTokenKey = 'refreshToken';
  private userKey = 'user';

  // Secure admin login
  async adminLogin(credentials: { email: string; password: string }): Promise<AuthResponse> {
    try {
      // Predefined admin credentials
      const validAdmins = [
        { email: 'admin@jivbook.com', password: 'admin123', phone: '+919999999999' },
        { email: 'dev@jivbook.com', password: 'dev123', phone: '+919999999998' }
      ];
      
      // Check if credentials match any valid admin
      const adminMatch = validAdmins.find(
        admin => admin.email.toLowerCase() === credentials.email.toLowerCase() 
              && admin.password === credentials.password
      );
      
      if (!adminMatch) {
        throw new Error('Invalid admin credentials');
      }
      
      // Use passwordless auth to get the actual user data
      const authPayload: LoginRequest = {
        name: 'Admin User',
        email: adminMatch.email,
        phone: adminMatch.phone,
        rememberMe: true
      };
      
      console.log('🔐 Attempting admin login with:', { email: adminMatch.email, phone: adminMatch.phone });
      
      const response = await apiClient.post<ApiResponse<AuthResponse>>('/auth', authPayload);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Authentication failed');
      }

      const { token, refreshToken, user } = response.data.data;
      
      console.log('✅ Admin login response:', { 
        userId: user._id, 
        isAdmin: user.isAdmin, 
        email: user.email 
      });
      
      // Verify the user is actually an admin
      if (!user.isAdmin) {
        console.error('❌ User is not an admin:', user);
        throw new Error('Access denied. Admin privileges required.');
      }
      
      // Store tokens and user data
      this.storeAuthData(token, refreshToken, user);
      
      return response.data.data;
    } catch (error: any) {
      console.error('❌ Admin login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Admin login failed';
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: errorMessage
      });
      throw new Error(errorMessage);
    }
  }

  // Store authentication data
  private storeAuthData(token: string, refreshToken: string, user: User): void {
    localStorage.setItem(this.tokenKey, token);
    localStorage.setItem(this.refreshTokenKey, refreshToken);
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  // Get stored access token
  getAccessToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  // Get stored refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  // Get stored user
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem(this.userKey);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem(this.userKey);
      }
    }
    return null;
  }

  // Check if user is logged in
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  // Check if user is admin
  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.isAdmin || false;
  }

  // Get current user's profile
  async getProfile(): Promise<User> {
    try {
      const response = await apiClient.get<ApiResponse<User>>('/profile');
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch profile');
      }

      const user = response.data.data;
      localStorage.setItem(this.userKey, JSON.stringify(user));
      
      return user;
    } catch (error: any) {
      console.error('Get profile error:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch profile');
    }
  }

  // Logout user
  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
  }
}

export const authService = new AuthService();
export default authService;

